import {
  getBrowserDefaultLanguage,
  getBrowserDefaultLocaleKey,
} from "@faire/retailer-visitor-shared/consts/LANGUAGE";
import { DEFAULT_LOCALE_KEY } from "@faire/web--source/common/consts/DEFAULT_LOCALE";
import { SHOP_PREVIEW_OVERRIDE_KEY } from "@faire/web--source/common/consts/SHOP_PREVIEW_OVERRIDE_KEY";
import { localeStringToLocaleKey } from "@faire/web--source/common/consts/TWO_TO_THREE_LETTER_LANGUAGE_CODE";
import { getCookies } from "@faire/web--source/common/cookies";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { getLocaleKey } from "@faire/web--source/common/serialized-data/getLocaleKey";
import logout from "@faire/web-api--source/endpoints/www/api/user/logout/post";
import { CapabilityEnum } from "@faire/web-api--source/faire/session/CapabilityEnum";
import { MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS } from "@faire/web-api--source/faire/session/CapabilityEnum/MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS";
import { RETAILER_PLACE_ORDER } from "@faire/web-api--source/faire/session/CapabilityEnum/RETAILER_PLACE_ORDER";
import { RETAILER_TRANSFER_OWNERSHIP } from "@faire/web-api--source/faire/session/CapabilityEnum/RETAILER_TRANSFER_OWNERSHIP";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { IRetailerAccountUserManagement } from "@faire/web-api--source/indigofair/retailer/IRetailerAccountUserManagement";

export const getUserStoreFieldsFromUser = (user: IUser | undefined) => {
  const browserDefaultLanguage = getBrowserDefaultLanguage();
  const browserDefaultLocaleKey = getBrowserDefaultLocaleKey();
  const localeKey = getLocaleKey();

  const userLocale =
    user?.locale_key?.language ??
    browserDefaultLanguage ??
    DEFAULT_LOCALE_KEY.language;
  const browserLevelLanguage = localeKey?.language ?? userLocale;
  const userLocaleKey = user?.locale_key ?? browserDefaultLocaleKey;
  const userCapabilities = user?.capabilities ?? [];
  const userHasSufficientCapability = (capability: CapabilityEnum) =>
    userCapabilities.includes(capability);
  const isRetailerSubUser = !userHasSufficientCapability(
    RETAILER_TRANSFER_OWNERSHIP
  );

  return {
    user,
    userToken: user?.token ?? "",
    fullName: getFullName(user),
    defaultAddress: user?.default_shipping_address ?? IAddress.build(),
    firstName: user?.first_name ?? "",
    lastName: user?.last_name ?? "",
    emailAddress: user?.email_address,
    phoneNumber: user?.phone_number,
    isEmployee: !!user?.is_employee,
    isImpersonating: !!user?.impersonating,
    userLocaleKey,
    browserLevelLanguage,
    browserLevelLocaleKey: getLocaleKey() ?? userLocaleKey,
    hasFaireEmailAddress: !!user?.email_address?.endsWith("@faire.com"),
    userHasSufficientCapability,
    isRetailerSubUser,
    isOwnerUser: userHasSufficientCapability(RETAILER_TRANSFER_OWNERSHIP),
    accountType: getAccountType(isRetailerSubUser, userHasSufficientCapability),
    userCanPlaceOrder: userHasSufficientCapability(RETAILER_PLACE_ORDER),
    userProfileImage: user?.profile_image,
    isBrandUser: user?.type === IUser.Type.BRAND_USER,
    isAdminUser: user?.type === IUser.Type.ADMIN,
  };
};

const getFullName = (user: IUser | undefined) => {
  if (!user?.first_name || !user?.last_name) {
    return "";
  }
  return [user.first_name, user.last_name].join(" ").trim();
};

export const browserLevelLocaleOverride = () => {
  const overrideLanguage = getCookies(SHOP_PREVIEW_OVERRIDE_KEY);
  if (
    !overrideLanguage ||
    !localeStringToLocaleKey(overrideLanguage).language
  ) {
    return undefined;
  }
  return localeStringToLocaleKey(overrideLanguage);
};

const getAccountType = (
  isRetailerSubUser: boolean,
  userHasSufficientCapability: (capability: CapabilityEnum) => boolean
) => {
  if (!isRetailerSubUser) {
    return IRetailerAccountUserManagement.Role.OWNER;
  }
  return userHasSufficientCapability(MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS)
    ? IRetailerAccountUserManagement.Role.ADMIN
    : IRetailerAccountUserManagement.Role.CONTRIBUTOR;
};

export const logOut = async (dest?: string) => {
  await logout();
  getLocationOrThrow().href = dest ? dest : "/";
};
